import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import ConversationsApi from '../../../../../api/conversations.api'
import MessagesApi from '../../../../../api/messages.api'
import { ToastContext } from '../../../../../context/ToastContext'
import { useQueryGetOrganizationTokensAvailable } from '../../../../../queries/organizationQueries'
import { isObjectEmpty } from '../../../../utils/object'

const TryLucaChatContext = createContext(null)

const EMPTY_CONVERSATION = Object.freeze({ id: null, messages: [] })
const NEW_CONVERSATION_LOADING_KEY = 'new-conversation'

const sanitizeConversationMessages = conversationMessages => {
  let filteredMessages = conversationMessages || []

  if (conversationMessages && conversationMessages.length > 0) {
    const validMessages = conversationMessages.filter(
      msg => msg != null && typeof msg === 'object' && msg.content
    )
    const userMessages = validMessages.filter(msg => msg && msg.isFromConsumer === true)
    const firstUserMessage = userMessages[0]

    if (
      userMessages.length === 1 &&
      firstUserMessage &&
      (firstUserMessage.content === 'Hola' || firstUserMessage.content === 'precarga respuestas') &&
      validMessages.length === 1
    ) {
      filteredMessages = []
    } else {
      filteredMessages = validMessages.filter(
        msg => msg && !(msg.content === 'precarga respuestas' && msg.isFromConsumer === true)
      )
    }
  }

  return filteredMessages
}

const getMessageTimestamp = message => {
  if (message.createdAt) {
    return new Date(message.createdAt).getTime()
  }

  if (message.id && message.id.startsWith('temp-')) {
    return parseInt(message.id.match(/temp-(\d+)-/)?.[1] || '0', 10)
  }

  return 0
}

const buildVisibleMessages = (conversation, previousMessages) => {
  const filteredMessages = sanitizeConversationMessages(conversation?.messages)
  const currentConversationId = conversation?.id || null
  const conversationUserMessages = new Map()

  filteredMessages.forEach(message => {
    if (message && message.isFromConsumer && message.content) {
      const key = message._id || message.id || message.content
      conversationUserMessages.set(key, message)
      conversationUserMessages.set(message.content, message)
    }
  })

  const tempMessages = previousMessages.filter(message => {
    if (!message || !message.id || !message.id.startsWith('temp-')) return false
    if ((message.conversationId || null) !== currentConversationId) return false
    if (!message.isFromConsumer) return true

    const messageId = message._id || message.id

    if (messageId && conversationUserMessages.has(messageId)) return false
    if (message.content && conversationUserMessages.has(message.content)) return false

    return true
  })

  return [...filteredMessages, ...tempMessages].sort(
    (leftMessage, rightMessage) =>
      getMessageTimestamp(leftMessage) - getMessageTimestamp(rightMessage)
  )
}

const normalizeConversation = conversation => ({
  ...EMPTY_CONVERSATION,
  ...(conversation || {}),
  messages: Array.isArray(conversation?.messages) ? conversation.messages : []
})

export const TryLucaChatProvider = ({ children }) => {
  const [conversations, setConversations] = useState([])
  const [loadingConversations, setLoadingConversations] = useState(false)
  const [activeConversation, setActiveConversation] = useState(EMPTY_CONVERSATION)
  const [messages, setMessages] = useState([])
  const [loadingConversationId, setLoadingConversationId] = useState(null)
  const [limitExceeded, setLimitExceeded] = useState(false)
  const [agentResponse, setAgentResponse] = useState({})
  const intervalRef = useRef(null)
  const activeConversationRef = useRef(EMPTY_CONVERSATION)
  const { showToast } = useContext(ToastContext)
  const { refetch: refetchTokensInfo } = useQueryGetOrganizationTokensAvailable()

  const isLoading =
    Boolean(loadingConversationId) &&
    (loadingConversationId === activeConversation.id ||
      (loadingConversationId === NEW_CONVERSATION_LOADING_KEY && !activeConversation.id))

  const updateConversations = useCallback(nextConversationsOrUpdater => {
    setConversations(previousConversations => {
      const safePreviousConversations = Array.isArray(previousConversations)
        ? previousConversations.filter(Boolean)
        : []
      const nextConversations =
        typeof nextConversationsOrUpdater === 'function'
          ? nextConversationsOrUpdater(safePreviousConversations)
          : nextConversationsOrUpdater

      return Array.isArray(nextConversations) ? nextConversations.filter(Boolean) : []
    })
  }, [])

  useEffect(() => {
    activeConversationRef.current = activeConversation
  }, [activeConversation])

  useEffect(() => {
    let isMounted = true

    const fetchConversations = async () => {
      setLoadingConversations(true)

      try {
        const response = await ConversationsApi.getConversations()

        if (!isMounted) return

        updateConversations(response)
      } catch {
        if (!isMounted) return

        showToast('error', 'Error al cargar las conversaciones de LUCA')
      } finally {
        if (isMounted) {
          setLoadingConversations(false)
        }
      }
    }

    fetchConversations()

    return () => {
      isMounted = false
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [showToast, updateConversations])

  useEffect(() => {
    setActiveConversation(currentConversation => {
      if (!currentConversation?.id) {
        return currentConversation
      }

      const matchingConversation = conversations.find(
        conversation => conversation?.id === currentConversation.id
      )

      if (!matchingConversation) {
        activeConversationRef.current = EMPTY_CONVERSATION
        return EMPTY_CONVERSATION
      }

      const nextConversation = normalizeConversation({
        ...matchingConversation,
        messages: matchingConversation.messages || currentConversation.messages || []
      })

      activeConversationRef.current = nextConversation
      return nextConversation
    })
  }, [conversations])

  useEffect(() => {
    setMessages(previousMessages => buildVisibleMessages(activeConversation, previousMessages))
  }, [activeConversation])

  const updateOrAddMessageToConversation = useCallback(
    message => {
      try {
        const currentConversation = normalizeConversation(activeConversationRef.current)
        const currentMessages = currentConversation.messages || []
        const messageId = message._id || message.id
        const existingMessageIndex = currentMessages.findIndex(currentMessage => {
          const currentMessageId = currentMessage._id || currentMessage.id
          return currentMessageId && messageId && currentMessageId === messageId
        })

        const messageToAdd = {
          ...message,
          id: message._id || message.id
        }

        const conversationMessages =
          existingMessageIndex >= 0
            ? currentMessages.map((currentMessage, index) =>
                index === existingMessageIndex ? messageToAdd : currentMessage
              )
            : [...currentMessages, messageToAdd]

        const nextConversation = {
          ...currentConversation,
          messages: conversationMessages
        }

        activeConversationRef.current = nextConversation
        setActiveConversation(nextConversation)

        if (currentConversation.id) {
          updateConversations(previousConversations =>
            previousConversations.map(conversation =>
              conversation.id === currentConversation.id
                ? { ...conversation, messages: conversationMessages }
                : conversation
            )
          )
        }
      } catch {
        showToast('error', 'Error al actualizar los mensajes de LUCA')
      }
    },
    [showToast, updateConversations]
  )

  useEffect(() => {
    if (
      isObjectEmpty(agentResponse) ||
      (activeConversation.id && agentResponse.conversationId !== activeConversation.id)
    ) {
      return
    }

    const completed =
      agentResponse.isComplete === true ||
      agentResponse.status === 'completed' ||
      agentResponse.status === 'error'

    updateOrAddMessageToConversation(agentResponse)

    if (completed) {
      setLoadingConversationId(currentLoadingConversationId =>
        currentLoadingConversationId === agentResponse.conversationId
          ? null
          : currentLoadingConversationId
      )
      refetchTokensInfo()

      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    setAgentResponse({})
  }, [activeConversation.id, agentResponse, refetchTokensInfo, updateOrAddMessageToConversation])

  useEffect(() => {
    if (!loadingConversationId || loadingConversationId === NEW_CONVERSATION_LOADING_KEY) {
      return undefined
    }

    intervalRef.current = setInterval(async () => {
      try {
        const response = await ConversationsApi.getAgentResponse(loadingConversationId)
        const agentMessage = response.message
        const completed = response.isComplete === true

        if (agentMessage && !isObjectEmpty(agentMessage)) {
          setAgentResponse({
            ...agentMessage,
            isComplete: completed
          })

          if (completed || agentMessage.status === 'completed' || agentMessage.status === 'error') {
            if (intervalRef.current) {
              clearInterval(intervalRef.current)
              intervalRef.current = null
            }
          }
        } else if (completed && intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
          setLoadingConversationId(currentLoadingConversationId =>
            currentLoadingConversationId === loadingConversationId
              ? null
              : currentLoadingConversationId
          )
        }
      } catch (error) {
        if (error.response?.status === 409) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
          }

          setLimitExceeded(true)
          setLoadingConversationId(currentLoadingConversationId =>
            currentLoadingConversationId === loadingConversationId
              ? null
              : currentLoadingConversationId
          )
          refetchTokensInfo()
          return
        }

        showToast('error', 'Error al obtener la respuesta del agente')
        setLoadingConversationId(currentLoadingConversationId =>
          currentLoadingConversationId === loadingConversationId
            ? null
            : currentLoadingConversationId
        )

        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      }
    }, 2000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [loadingConversationId, refetchTokensInfo, showToast])

  const selectConversation = useCallback(conversation => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    setLoadingConversationId(null)
    const nextConversation = normalizeConversation(conversation)
    activeConversationRef.current = nextConversation
    setActiveConversation(nextConversation)
  }, [])

  const createNewConversation = useCallback(async files => {
    try {
      const nextConversation = await ConversationsApi.createConversation(files.message, files.attachments)

      setMessages(previousMessages =>
        previousMessages.map(message =>
          message.id?.startsWith('temp-') && (message.conversationId || null) === null
            ? { ...message, conversationId: nextConversation.id }
            : message
        )
      )

      updateConversations(previousConversations => [...previousConversations, nextConversation])
      activeConversationRef.current = normalizeConversation(nextConversation)
      setActiveConversation(normalizeConversation(nextConversation))
      setLoadingConversationId(currentLoadingConversationId =>
        currentLoadingConversationId === NEW_CONVERSATION_LOADING_KEY
          ? nextConversation.id
          : currentLoadingConversationId
      )

      return nextConversation
    } catch (error) {
      if (error.response?.status === 409) {
        setLimitExceeded(true)
        setLoadingConversationId(null)
        refetchTokensInfo()
      }

      throw error
    }
  }, [refetchTokensInfo, updateConversations])

  const addMessagesToState = useCallback((messageContent, files) => {
    const currentConversation = activeConversationRef.current
    const consumerMessage = {
      id: `temp-${Date.now()}-${Math.random()}`,
      conversationId: currentConversation?.id || null,
      content: messageContent,
      isFromConsumer: true,
      files: files.map(file => file.name)
    }

    setMessages(previousMessages => [...previousMessages, consumerMessage])
  }, [])

  const sendMessage = useCallback(
    async (messageContent, files = []) => {
      const trimmedMessage = messageContent.trim()

      if (trimmedMessage === '') {
        return { success: false, reason: 'empty' }
      }

      const currentConversation = normalizeConversation(activeConversationRef.current)
      const isCreatingConversation = !currentConversation.id

      setLimitExceeded(false)
      setLoadingConversationId(currentConversation.id || NEW_CONVERSATION_LOADING_KEY)
      addMessagesToState(trimmedMessage, files)

      try {
        if (isCreatingConversation) {
          await createNewConversation({ message: trimmedMessage, attachments: files })
        } else {
          const { message } = await MessagesApi.sendMessage(
            { content: trimmedMessage, conversationId: currentConversation.id },
            files
          )

          updateOrAddMessageToConversation(message)
        }

        return { success: true }
      } catch (error) {
        setMessages(previousMessages => previousMessages.slice(0, -1))

        if (error.response?.status === 409) {
          setLoadingConversationId(null)
          return { success: false, reason: 'limit' }
        }

        setLoadingConversationId(null)
        showToast('error', 'Error al enviar el mensaje, intenta de nuevo')
        return { success: false, reason: 'error' }
      }
    },
    [addMessagesToState, createNewConversation, showToast, updateOrAddMessageToConversation]
  )

  const resetActiveConversation = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    activeConversationRef.current = EMPTY_CONVERSATION
    setActiveConversation(EMPTY_CONVERSATION)
    setMessages([])
    setAgentResponse({})
    setLoadingConversationId(null)
  }, [])

  const value = useMemo(
    () => ({
      activeConversation,
      conversations,
      isLoading,
      limitExceeded,
      loadingConversations,
      messages,
      resetActiveConversation,
      selectConversation,
      sendMessage,
      setConversations: updateConversations,
      setLimitExceeded
    }),
    [
      activeConversation,
      conversations,
      isLoading,
      limitExceeded,
      loadingConversations,
      messages,
      resetActiveConversation,
      selectConversation,
      sendMessage,
      updateConversations
    ]
  )

  return <TryLucaChatContext.Provider value={value}>{children}</TryLucaChatContext.Provider>
}

export const useTryLucaChat = () => {
  const context = useContext(TryLucaChatContext)

  if (!context) {
    throw new Error('useTryLucaChat debe usarse dentro de TryLucaChatProvider')
  }

  return context
}
